import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../config/api';
import { useAuth } from './AuthContext';

const NotificationsContext = createContext(null);

export function NotificationsProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [counts, setCounts] = useState({ unread: 0, expiry: 0, low_stock: 0, damage: 0 });
  const seenIds = useRef(new Set());

  const refresh = useCallback(async ({ announce = false } = {}) => {
    if (!isAuthenticated) return;

    try {
      const data = await api.get('/notifications?limit=30');
      const list = data?.notifications || [];
      if (announce && seenIds.current.size) {
        const newAlert = list.find((item) => !seenIds.current.has(item.id) && !item.is_read);
        if (newAlert) toast(`${newAlert.title}: ${newAlert.message}`, { icon: '⚠️' });
      }
      seenIds.current = new Set(list.map((item) => item.id));
      setNotifications(list);
      setCounts(data?.counts || { unread: 0, expiry: 0, low_stock: 0, damage: 0 });
    } catch {
      // Notifications are an enhancement; retain last known state if unavailable.
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    refresh();
    const interval = window.setInterval(() => refresh({ announce: true }), 30000);
    const listener = () => refresh({ announce: true });
    window.addEventListener('pharmacy-notifications-changed', listener);
    return () => { window.clearInterval(interval); window.removeEventListener('pharmacy-notifications-changed', listener); };
  }, [isAuthenticated, refresh]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)));
      setCounts((prev) => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
    } catch (e) {
      toast.error('Failed to mark as read');
    }
  };
  const markAllRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      setCounts((prev) => ({ ...prev, unread: 0 }));
      toast.success('All notifications marked as read');
    } catch (e) {
      toast.error('Failed to mark all as read');
    }
  };

  return <NotificationsContext.Provider value={{ notifications, counts, refresh, markRead, markAllRead }}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (!context) throw new Error('useNotifications must be used within NotificationsProvider');
  return context;
}
